/**
 * What is attached to one expense: the thumbnails, and the way to add another.
 *
 * The adding half — the picker, the phone's camera, the scanner, the staging
 * list and the quota bar — is `ReceiptUploader`, which the Receipts tab uses
 * too. What is here is the part that is specific to one expense: the list of
 * what is on it, taking one off, and the fact that this panel knows the
 * transaction that everything it stores is filed against straight away.
 *
 * **Every image is `<img src="/api/receipts/:id/file?variant=thumb">`.** A
 * path through the Worker, re-authorised on every read, never a bucket URL and
 * never a signed one. Opening one goes to the group's receipt viewer rather
 * than to the raw file in a new tab: it is the same picture with the expense
 * under it, download and delete beside it, and a back gesture that comes back
 * here.
 */

import type { Receipt } from '../api'
import { formatBytes } from '../lib/format'
import { nameOf, tileFor } from '../lib/receipt-library'
import { useAttachReceipt, useReceipts, useRemoveReceipt } from '../queries'
import type { Storage } from '../api'
import { ReceiptUploader } from './ReceiptUploader'

export function Receipts({
  groupId,
  transactionId,
  description,
  storage,
  canUpload,
  archived,
  onOpen,
}: {
  groupId: string
  transactionId: string
  /** The expense's own description, so every control can name what it is on. */
  description: string
  /** From `GET /api/me`, or `null` while it is still being fetched. */
  storage: Storage | null
  /** `can_upload_attachments`, as the resolver reported it. */
  canUpload: boolean
  archived: boolean
  /** Opens one full-bleed, in the group's receipt viewer. */
  onOpen: (receiptId: string) => void
}) {
  const receipts = useReceipts(groupId, transactionId, true)
  const attach = useAttachReceipt(groupId, transactionId)
  const remove = useRemoveReceipt(groupId, transactionId)

  return (
    <div className="receipts">
      <h4 className="receipts__heading">
        Receipts<span className="visually-hidden"> for {description}</span>
      </h4>

      {receipts.isPending && <p className="muted">Looking for what is attached…</p>}
      {receipts.isError && <p className="bad">{receipts.error.message}</p>}
      {receipts.data?.receipts.length === 0 && (
        <p className="muted">Nothing is attached to this one yet.</p>
      )}

      <ul className="receipts__list">
        {receipts.data?.receipts.map((receipt) => (
          <li key={receipt.id} className="receipts__item">
            <button
              type="button"
              className="receipts__open"
              onClick={() => onOpen(receipt.id)}
              title={nameOf(receipt)}
            >
              <Thumbnail receipt={receipt} />
              <span className="receipts__name">{nameOf(receipt)}</span>
              <span className="receipts__size">{formatBytes(receipt.sizeBytes)}</span>
            </button>
            <button
              type="button"
              className="button--quiet receipts__remove"
              disabled={remove.isPending || archived}
              onClick={() => remove.mutate(receipt.id)}
            >
              Remove<span className="visually-hidden"> {nameOf(receipt)}</span>
            </button>
          </li>
        ))}
      </ul>

      {remove.isError && <p className="bad">{remove.error.message}</p>}

      <ReceiptUploader
        send={(prepared) => attach.mutateAsync(prepared)}
        storage={storage}
        canUpload={canUpload}
        archived={archived}
        what={description}
      />
    </div>
  )
}

/**
 * The tile. `tileFor` is what decides the `src`, and it cannot return the
 * full-size file — a list of thumbnails that fell back to the original when
 * there was no thumbnail would download megabytes to draw something the size
 * of a stamp. No thumbnail is a fact about the file, not an error.
 */
export function Thumbnail({ receipt }: { receipt: Receipt }) {
  const tile = tileFor(receipt)

  if (tile.kind === 'thumbnail') {
    return (
      <img
        className="receipts__thumb"
        src={tile.src}
        alt={tile.alt}
        loading="lazy"
        decoding="async"
        width={120}
        height={120}
      />
    )
  }
  return (
    <span className="receipts__thumb receipts__thumb--none" role="img" aria-label={tile.alt}>
      <span aria-hidden="true">{tile.glyph}</span>
    </span>
  )
}
